import { AnalyticsContext } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addError } from '@shared/utils/logger';
import { useContext } from 'react';
import {
  AutoPopulateAccommodationPlacesPayloadV5,
  ScoringPriorityField,
  useAutoPopulateAccommodationPlacesV5Mutation,
  Maybe,
  AutopopulateAccommodationPlacesScenario,
} from '@graphql/generated';
import { parseNetworkErrorDetails } from '@shared/utils/parseNetworkErrorDetails';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';

const OPERATION_NAME = 'autoPopulationApiV5';

interface CallAutoPopulationApiV5Payload
  extends Readonly<{
    eventId?: string | null;
    location: Readonly<{ latitude: number; longitude: number }>;
    scoringPriorityFields: ReadonlyArray<ScoringPriorityField>;
    context: string;
    searchRadiusKm: number;
    venueGooglePlaceId?: string;
    scenario?: AutopopulateAccommodationPlacesScenario;
  }> {}

type AutoPopulationApiV5Response = Readonly<{
  idempotentId?: string;
  status?: string;
  error?: Maybe<string>;
}>;

/**
 * Custom hook to call the auto-populate accommodation places v5 mutation.
 * @returns A tuple containing the callAutoPopulationApiV5 function and the loading state.
 */
export const useAutoPopulationApiV5 = () => {
  const analytics = useContext(AnalyticsContext);
  const { language: locale } = useLocaleContext();

  const [autoPopulateAccommodationPlacesV5, { loading }] = useAutoPopulateAccommodationPlacesV5Mutation();

  const callAutoPopulationApiV5 = useEventCallback(async (payloadArgs: CallAutoPopulationApiV5Payload): Promise<AutoPopulationApiV5Response> => {
    const { eventId, location, scoringPriorityFields, context, searchRadiusKm, venueGooglePlaceId, scenario } = payloadArgs;

    if (!eventId) {
      const error = new Error('Missing event id');
      addError(error, {
        context,
        latitude: location?.latitude,
        longitude: location?.longitude,
        isAsync: true,
        scenario,
        operation: OPERATION_NAME,
      });
      return { error: error.message };
    }

    const payload: AutoPopulateAccommodationPlacesPayloadV5 = {
      eventId,
      force: true,
      location,
      searchRadiusKm,
      locale,
      scoringPriorityFields: [...scoringPriorityFields],
      venueGooglePlaceId: venueGooglePlaceId,
      scenario,
    };

    try {
      const response = await autoPopulateAccommodationPlacesV5({ variables: { payload } });

      if (!response.data || !response.data.autoPopulateAccommodationPlacesV5) {
        const error = 'No response data';
        addError(new Error(error), {
          context,
          eventId: payload.eventId,
          latitude: payload.location?.latitude,
          longitude: payload.location?.longitude,
          isAsync: true,
          scenario,
          operation: OPERATION_NAME,
        });
        return { error };
      }

      const { status, error } = response.data.autoPopulateAccommodationPlacesV5;

      analytics.track({
        category: 'accommodations',
        action: `TravelButtonInteracted`,
        label: 'autoPopulateAccommodationPlaces',
        extraInfo: {
          name: `autoPopulateAccommodationPlaces`,
          eventId: payload.eventId,
          latitude: payload.location?.latitude,
          longitude: payload.location?.longitude,
          isAsync: true,
          scenario,
          source: context,
          status,
        },
      });

      return { status, error };
    } catch (error) {
      const { errorExplanation, statusCode, isTimeout } = parseNetworkErrorDetails(error);
      addError(errorExplanation, {
        context,
        eventId: payload.eventId,
        latitude: payload.location?.latitude,
        longitude: payload.location?.longitude,
        isAsync: true,
        scenario,
        statusCode,
        isTimeout,
        originalError: error,
        operation: OPERATION_NAME,
      });
      throw error;
    }
  });

  return { callAutoPopulationApiV5, loading };
};
